/**
 * AsignarTicketDialog (rc45) — Modal pa asignar un ticket a OTRO usuario
 * activo (no solo self-assign).
 *
 * Lista users activos, búsqueda por nombre/email, click pa asignar.
 * Backend ya soporta `POST /tickets/{id}/assign` con cualquier user_id.
 *
 * Permiso: tickets.write (el mismo que ya requiere la pantalla).
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Search, UserPlus, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiCall } from "@/lib/api";
import { useAssignTicket } from "./api";
import type { TicketDetail } from "./schemas";

type UserMini = {
  id: string;
  email: string;
  nombre_completo: string | null;
  rol_nombre: string | null;
  activo: boolean;
};

type PagedUsers = { items: UserMini[]; total: number };

function useUsersActivos() {
  return useQuery({
    queryKey: ["users-activos-asignacion"],
    queryFn: () =>
      apiCall<PagedUsers>("get", "/api/v1/users", {
        searchParams: { activo: true, size: 200 },
      }),
  });
}

function avatarFromName(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  ticket: TicketDetail;
};

export function AsignarTicketDialog({ open, onOpenChange, ticket }: Props) {
  const usersQ = useUsersActivos();
  const assign = useAssignTicket(ticket.id);
  const [search, setSearch] = useState("");

  const usuarios = usersQ.data?.items ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return usuarios;
    return usuarios.filter((u) => {
      const haystack = [
        u.nombre_completo ?? "",
        u.email,
        u.rol_nombre ?? "",
      ].join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [usuarios, search]);

  async function assignTo(userId: string, userName: string) {
    try {
      await assign.mutateAsync(userId);
      toast.success(`Ticket asignado a ${userName}`);
      onOpenChange(false);
      setSearch("");
    } catch (e: any) {
      const detail = e?.response?.data?.detail ?? e?.message;
      toast.error(detail ?? "Error al asignar");
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) setSearch("");
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus size={16} />
            Asignar ticket {ticket.codigo}
          </DialogTitle>
          <DialogDescription>
            Selecciona el usuario al que se le asignará este ticket. Recibirá un push
            notification si tiene la app iOS instalada.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Search
              size={14}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              autoFocus
              placeholder="Buscar por nombre, email o rol…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>

          <div className="max-h-80 overflow-y-auto rounded-md border">
            {usersQ.isLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 size={16} className="animate-spin text-muted-foreground" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-6 text-center text-xs text-muted-foreground">
                {search ? "No coincide ningún usuario activo." : "Sin usuarios activos."}
              </div>
            ) : (
              <ul className="divide-y">
                {filtered.map((u) => {
                  const isCurrent = ticket.asignado?.id === u.id;
                  const displayName = u.nombre_completo || u.email;
                  return (
                    <li key={u.id}>
                      <button
                        type="button"
                        onClick={() => assignTo(u.id, displayName)}
                        disabled={isCurrent || assign.isPending}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left transition hover:bg-muted/50 disabled:opacity-50"
                      >
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-violet-500 text-[10px] font-bold text-white">
                          {avatarFromName(displayName)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm">{displayName}</div>
                          <div className="truncate text-[11px] text-muted-foreground">
                            {u.email}
                            {u.rol_nombre ? ` · ${u.rol_nombre}` : ""}
                          </div>
                        </div>
                        {isCurrent && (
                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] text-emerald-700">
                            Asignado
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {ticket.asignado && (
            <button
              type="button"
              onClick={async () => {
                await assign.mutateAsync(null);
                toast.success("Ticket desasignado");
                onOpenChange(false);
              }}
              className="flex items-center gap-1.5 text-xs text-rose-600 hover:text-rose-700"
              disabled={assign.isPending}
            >
              <X size={12} /> Quitar asignación actual ({ticket.asignado.nombre})
            </button>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
